import type { JSX } from 'react';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { pluralize } from '../lib/format.js';
import { api, ApiError } from '../lib/api.js';
import { ItemCard } from '../components/ItemCard.js';
import { Avatar, Button, EmptyState, Notice } from '../components/ui.js';
import { SkeletonHeader, SkeletonItems } from '../components/Skeleton.js';
import { IconClock, IconEmptyBox, IconGift, IconLink } from '../components/icons.js';
import { useAuth } from '../lib/auth.js';

/**
 * Список, открытый по секретной ссылке.
 *
 * Единственная страница, доступная без входа: ссылкой делятся в мессенджере,
 * и требовать регистрации только ради того, чтобы посмотреть чужой вишлист,
 * — верный способ потерять человека на первом шаге.
 *
 * Бронировать без аккаунта всё же нельзя: бронь должна быть за кем-то закреплена,
 * иначе её невозможно ни отменить, ни показать в «Я дарю».
 */

export function SharePage(): JSX.Element {
  const { token = '' } = useParams();

  /*
   * Прячем страницу от поисковых роботов.
   *
   * robots.txt закрывает /s/ целиком, но на него полагаться нельзя: часть
   * роботов приходит по ссылке из переписки и правила читает не всегда,
   * а панели браузеров и мессенджеров умеют отправлять открытые адреса
   * на индексацию сами. Метка noindex действует на самой странице —
   * это второй замок на той же двери.
   *
   * Метка снимается при уходе со страницы: приложение одностраничное,
   * и оставленный noindex закрыл бы от поиска и витрину тоже.
   */
  useEffect(() => {
    const мета = document.createElement('meta');
    мета.name = 'robots';
    мета.content = 'noindex, nofollow';
    document.head.appendChild(мета);
    return () => мета.remove();
  }, []);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const addFriend = useMutation({
    mutationFn: (userId: string) => api.friends.request({ userId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['share', token] }),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['share', token],
    queryFn: () => api.lists.byShareToken(token),
    enabled: Boolean(token),
  });

  /*
   * Верхняя полоса на этой странице своя: сюда приходят по ссылке, часто вообще
   * не зная, что такое Загадарь. Пока список грузился, человек видел голый
   * кружок на пустом фоне — ни имени, ни признака, что он попал куда надо.
   * Полоса от данных не зависит, поэтому написана один раз и стоит с первого
   * мига, а на месте списка — заготовка.
   */
  const шапкаСайта = (
    <header className="topbar">
      <a href="/" className="topbar__brand">
        <span className="topbar__mark" aria-hidden="true">
          ✦
        </span>
        Загадарь
      </a>
      {!user && (
        <a href="/">
          <Button size="sm" variant="primary">
            Завести свой список
          </Button>
        </a>
      )}
    </header>
  );

  if (isLoading) {
    return (
      <div className="app-shell">
        {шапкаСайта}
        <main className="app-main">
          <SkeletonHeader />
          <SkeletonItems />
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="app-shell">
        {шапкаСайта}
        <main className="app-main">
          <EmptyState
            icon={<IconLink />}
            title="Ссылка не работает"
            text={
              error instanceof ApiError && error.status === 404
                ? 'Возможно, владелец выпустил новую ссылку или удалил список.'
                : 'Не удалось открыть список по этой ссылке.'
            }
          />
        </main>
      </div>
    );
  }

  const { list, items } = data;

  return (
    <div className="app-shell">
      {шапкаСайта}

      <main className="app-main">
        <div className="page-header">
          <div className="row">
            <span style={{ fontSize: '2rem' }} aria-hidden="true">
              {list.coverEmoji || '🎁'}
            </span>
            <div>
              <h1>{list.title}</h1>
              <div className="row tiny muted" style={{ gap: 6 }}>
                <Avatar name={list.owner.displayName} src={list.owner.avatarUrl} size="sm" />
                {list.owner.displayName} · {pluralize(list.itemCount, 'желание', 'желания', 'желаний')}
              </div>
            </div>
          </div>
        </div>

        {list.description && <p className="muted">{list.description}</p>}

        {/*
          Замыкаем естественный путь: человеку прислали ссылку на список, он его
          открыл — и тут же может добавиться в друзья. Без этого пришлось бы
          отдельно узнавать имя пользователя и искать его вручную, а на таком
          шаге люди просто отваливаются.
        */}
        {user && !list.viewerIsOwner && list.owner.friendship?.status !== 'accepted' && (
          <div style={{ margin: 'var(--space-4) 0' }}>
            {list.owner.friendship?.status === 'pending' ? (
              <Notice tone="info" icon={<IconClock size={19} />}>
                <span className="small">
                  {list.owner.friendship.outgoing
                    ? `Заявка отправлена. Когда ${list.owner.displayName} её примет, вы увидите и другие списки.`
                    : `${list.owner.displayName} хочет добавить вас в друзья — ответьте в разделе «Друзья».`}
                </span>
              </Notice>
            ) : (
              <div className="row row--wrap" style={{ gap: 'var(--space-3)' }}>
                <Button
                  variant="primary"
                  loading={addFriend.isPending}
                  onClick={() => addFriend.mutate(list.owner.id)}
                >
                  Добавить {list.owner.displayName} в друзья
                </Button>
                <span className="tiny faint">Тогда увидите и остальные списки — а бронь останется тайной</span>
              </div>
            )}
          </div>
        )}

        {!user && (
          <div style={{ margin: 'var(--space-4) 0' }}>
            <Notice tone="info" icon={<IconGift size={19} />}>
              <span className="small">
                Войдите, чтобы забронировать подарок — тогда никто не купит то же самое, а получатель об этом не узнает.
              </span>
            </Notice>
          </div>
        )}

        {items.length === 0 ? (
          <EmptyState icon={<IconEmptyBox />} title="Список пока пуст" text="Владелец ещё ничего не добавил." />
        ) : (
          <div className="items-grid" style={{ marginTop: 'var(--space-5)' }}>
            {items.map((item) => (
              <ItemCard key={item.id} item={item} isOwner={list.viewerIsOwner} canReserve={list.viewerCanReserve} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
